<script setup>
import { h, ref, computed, onMounted, watch } from 'vue';
import { provideSidebarContext, useSidebarResize } from './provider';
import { useAccount } from 'dashboard/composables/useAccount';
import { useKbd } from 'dashboard/composables/utils/useKbd';
import { useMapGetter } from 'dashboard/composables/store';
import { useFandataPerfil } from 'dashboard/composables/useFandataPerfil';
import {
  perfilVeSecao,
  perfilVeFilho,
} from 'dashboard/helper/fandataPerfis';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useSidebarKeyboardShortcuts } from './useSidebarKeyboardShortcuts';
import { vOnClickOutside } from '@vueuse/components';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useWindowSize, useEventListener } from '@vueuse/core';

import Button from 'dashboard/components-next/button/Button.vue';
import SidebarGroup from './SidebarGroup.vue';
import SidebarProfileMenu from './SidebarProfileMenu.vue';
import SidebarChangelogCard from './SidebarChangelogCard.vue';
import SidebarChangelogButton from './SidebarChangelogButton.vue';
import ChannelLeaf from './ChannelLeaf.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import SidebarAccountSwitcher from './SidebarAccountSwitcher.vue';
import Logo from 'next/icon/Logo.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import NotificationsAPI from 'dashboard/api/notifications';
import {
  SIDEBAR_SORT_SECTIONS,
  getSidebarSortOptions,
  resolveSidebarSort,
  sortSidebarItems,
} from 'dashboard/helper/sidebarSort';

// Gating "EM BREVE" por ambiente (runtime): demo trava os painéis; dev/prod mostram real.
const fandataGated = ['true', true].includes(
  window.globalConfig?.FANDATA_DEMO_GATING
);

const props = defineProps({
  isMobileSidebarOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'closeKeyShortcutModal',
  'openKeyShortcutModal',
  'showCreateAccountModal',
  'closeMobileSidebar',
]);

const { accountScopedRoute, isOnChatwootCloud, currentAccount } = useAccount();
const store = useStore();
const searchShortcut = useKbd([`$mod`, 'k']);
const { t } = useI18n();

const isACustomBrandedInstance = useMapGetter(
  'globalConfig/isACustomBrandedInstance'
);
const isRTL = useMapGetter('accounts/isRTL');

const { width: windowWidth } = useWindowSize();
const isMobile = computed(() => windowWidth.value < 768);

const accountId = useMapGetter('getCurrentAccountId');
const currentUserId = useMapGetter('getCurrentUserID');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const hasAdvancedAssignment = computed(() => {
  return isFeatureEnabledonAccount.value(
    accountId.value,
    FEATURE_FLAGS.ADVANCED_ASSIGNMENT
  );
});

const hasConversationUnreadCounts = computed(() => {
  return isFeatureEnabledonAccount.value(
    accountId.value,
    FEATURE_FLAGS.CONVERSATION_UNREAD_COUNTS
  );
});

const fetchConversationUnreadCounts = ([currentAccountId, isEnabled]) => {
  if (!currentAccountId) return;

  if (!isEnabled) {
    store.dispatch('conversationUnreadCounts/clear');
    return;
  }

  store.dispatch('conversationUnreadCounts/get');
};

const fetchSidebarSortPreferences = ([currentAccountId, userId]) => {
  if (!currentAccountId || !userId) return;
  store.dispatch('sidebarSortPreferences/initialize');
};

const toggleShortcutModalFn = show => {
  if (show) {
    emit('openKeyShortcutModal');
  } else {
    emit('closeKeyShortcutModal');
  }
};

useSidebarKeyboardShortcuts(toggleShortcutModalFn);

const expandedItem = ref(null);

const setExpandedItem = name => {
  expandedItem.value = expandedItem.value === name ? null : name;
};

const {
  sidebarWidth,
  isCollapsed,
  setSidebarWidth,
  saveWidth,
  snapToCollapsed,
  snapToExpanded,
  COLLAPSED_THRESHOLD,
} = useSidebarResize();

// On mobile, sidebar is always expanded (flyout mode)
const isEffectivelyCollapsed = computed(
  () => !isMobile.value && isCollapsed.value
);

// Resize handle logic
const isResizing = ref(false);
const startX = ref(0);
const startWidth = ref(0);

provideSidebarContext({
  expandedItem,
  setExpandedItem,
  isCollapsed: isEffectivelyCollapsed,
  sidebarWidth,
  isResizing,
});

// Get clientX from mouse or touch event
const getClientX = event =>
  event.touches ? event.touches[0].clientX : event.clientX;

const onResizeStart = event => {
  isResizing.value = true;
  startX.value = getClientX(event);
  startWidth.value = sidebarWidth.value;
  Object.assign(document.body.style, {
    cursor: 'col-resize',
    userSelect: 'none',
  });
  // Prevent default to avoid scrolling on touch
  event.preventDefault();
};

const onResizeMove = event => {
  if (!isResizing.value) return;

  const delta = isRTL.value
    ? startX.value - getClientX(event)
    : getClientX(event) - startX.value;
  setSidebarWidth(startWidth.value + delta);
};

const onResizeEnd = () => {
  if (!isResizing.value) return;

  isResizing.value = false;
  Object.assign(document.body.style, { cursor: '', userSelect: '' });

  // Snap to collapsed state if below threshold
  if (sidebarWidth.value < COLLAPSED_THRESHOLD) {
    snapToCollapsed();
  } else {
    saveWidth();
  }
};

const onResizeHandleDoubleClick = () => {
  if (isCollapsed.value) snapToExpanded();
  else snapToCollapsed();
};

// Support both mouse and touch events
useEventListener(document, 'mousemove', onResizeMove);
useEventListener(document, 'mouseup', onResizeEnd);
useEventListener(document, 'touchmove', onResizeMove, { passive: false });
useEventListener(document, 'touchend', onResizeEnd);

const inboxes = useMapGetter('inboxes/getInboxes');
const labels = useMapGetter('labels/getLabelsOnSidebar');
const allUnreadCount = useMapGetter(
  'conversationUnreadCounts/getAllUnreadCount'
);
const getInboxUnreadCount = useMapGetter(
  'conversationUnreadCounts/getInboxUnreadCount'
);
const getLabelUnreadCount = useMapGetter(
  'conversationUnreadCounts/getLabelUnreadCount'
);
const getTeamUnreadCount = useMapGetter(
  'conversationUnreadCounts/getTeamUnreadCount'
);
const teams = useMapGetter('teams/getMyTeams');
const contactCustomViews = useMapGetter('customViews/getContactCustomViews');
const conversationCustomViews = useMapGetter(
  'customViews/getConversationCustomViews'
);
const getSidebarSectionSort = useMapGetter(
  'sidebarSortPreferences/getSectionSort'
);

// Contador de MENÇÕES não-lidas (notificações do tipo conversation_mention).
const notificationUnreadCount = useMapGetter('notifications/getUnreadCount');
const mentionUnreadCount = ref(0);
const refreshMentionUnreadCount = async () => {
  try {
    const { data } = await NotificationsAPI.getMentionUnreadCount();
    mentionUnreadCount.value = Number(data) || 0;
  } catch (error) {
    // silencioso — não bloqueia o menu
  }
};

onMounted(() => {
  store.dispatch('labels/get');
  store.dispatch('inboxes/get');
  store.dispatch('notifications/unReadCount');
  refreshMentionUnreadCount();
  store.dispatch('teams/get');
  store.dispatch('attributes/get');
  store.dispatch('customViews/get', 'conversation');
  store.dispatch('customViews/get', 'contact');
});

watch([accountId, hasConversationUnreadCounts], fetchConversationUnreadCounts, {
  immediate: true,
});

watch([accountId, currentUserId], fetchSidebarSortPreferences, {
  immediate: true,
});

// Recarrega a contagem de menções não-lidas quando o total de notificações muda (nova menção / lida).
watch(notificationUnreadCount, refreshMentionUnreadCount);

const getSortOptionsForSection = section =>
  getSidebarSortOptions(section, {
    hasUnreadCounts: hasConversationUnreadCounts.value,
  });

const getSortForSection = section =>
  resolveSidebarSort(section, getSidebarSectionSort.value(section), {
    hasUnreadCounts: hasConversationUnreadCounts.value,
  });

const updateSortPreference = (section, sortBy) => {
  store.dispatch('sidebarSortPreferences/setSectionSort', {
    section,
    sortBy,
  });
};

const buildSortConfig = section => ({
  sortOptions: getSortOptionsForSection(section),
  activeSort: getSortForSection(section),
  onSortChange: sortBy => updateSortPreference(section, sortBy),
});

const sortedFolders = computed(() =>
  sortSidebarItems(conversationCustomViews.value, {
    sortBy: getSortForSection(SIDEBAR_SORT_SECTIONS.FOLDERS),
    labelKey: view => view.name,
  })
);

const sortedTeams = computed(() =>
  sortSidebarItems(teams.value, {
    sortBy: getSortForSection(SIDEBAR_SORT_SECTIONS.TEAMS),
    labelKey: team => team.name,
    unreadCountKey: team => getTeamUnreadCount.value(team.id),
  })
);

const sortedInboxes = computed(() =>
  sortSidebarItems(inboxes.value, {
    sortBy: getSortForSection(SIDEBAR_SORT_SECTIONS.CHANNELS),
    labelKey: inbox => inbox.name,
    unreadCountKey: inbox => getInboxUnreadCount.value(inbox.id),
  })
);

// T-165: os canais separados por USO. Receptivo e ativo não se parecem: num o torcedor procura o
// clube e o relógio de resposta é nosso; no outro o clube procura o torcedor, o custo é por
// mensagem e a régua de sucesso é outra. Numa lista só, a supervisão compara volume que não se
// compara.
//
// Enquanto ninguém classificar nada, a lista fica exatamente como era: um grupo com todos os
// canais, sem subgrupo nenhum. A separação só aparece quando existe canal ativo de verdade.
const USOS_ATIVOS = ['ativo_hsm', 'ativo_email'];

const folhaDoCanal = inbox => ({
  name: `${inbox.name}-${inbox.id}`,
  label: inbox.name,
  badgeCount: getInboxUnreadCount.value(inbox.id),
  icon: h(ChannelIcon, { inbox, class: 'size-[16px]' }),
  to: accountScopedRoute('inbox_dashboard', { inbox_id: inbox.id }),
  component: leafProps =>
    h(ChannelLeaf, {
      label: leafProps.label,
      active: leafProps.active,
      inbox,
      badgeCount: leafProps.badgeCount,
    }),
});

const canaisPorUso = computed(() => {
  const mapa = currentAccount.value?.fandata_canais_uso || {};
  const usoDe = inbox => mapa[String(inbox.id)] || 'receptivo';
  const temAtivo = sortedInboxes.value.some(inbox =>
    USOS_ATIVOS.includes(usoDe(inbox))
  );

  if (!temAtivo) return sortedInboxes.value.map(folhaDoCanal);

  const grupo = (uso, chaveLabel) => {
    const lista = sortedInboxes.value.filter(inbox => usoDe(inbox) === uso);
    if (!lista.length) return null;
    return {
      name: `Channels-${uso}`,
      label: t(chaveLabel),
      icon: 'i-lucide-mailbox',
      children: lista.map(folhaDoCanal),
    };
  };

  return [
    grupo('receptivo', 'SIDEBAR.CANAIS_RECEPTIVOS'),
    grupo('ativo_hsm', 'SIDEBAR.CANAIS_ATIVO_HSM'),
    grupo('ativo_email', 'SIDEBAR.CANAIS_ATIVO_EMAIL'),
  ].filter(Boolean);
});

const sortedLabels = computed(() =>
  sortSidebarItems(labels.value, {
    sortBy: getSortForSection(SIDEBAR_SORT_SECTIONS.LABELS),
    labelKey: label => label.title,
    unreadCountKey: label => getLabelUnreadCount.value(label.id),
  })
);

const closeMobileSidebar = () => {
  if (!props.isMobileSidebarOpen) return;
  emit('closeMobileSidebar');
};

// T-151: as quatro visões gerais (agente, etiqueta, caixa e time) eram a MESMA página com um
// agrupamento diferente, e viraram uma entrada só com seletor de dimensão. As telas de detalhe
// continuam existindo e mantêm o item aceso quando se abre uma delas.
const newReportRoutes = () => [
  {
    name: 'Reports Quebras',
    label: t('SIDEBAR.REPORTS_QUEBRAS'),
    to: accountScopedRoute('quebras_reports'),
    activeOn: [
      'agent_reports_show',
      'inbox_reports_show',
      'team_reports_show',
      'label_reports_show',
    ],
  },
];

const reportRoutes = computed(() => newReportRoutes());

// T-145: a Tamara pediu que a visão do agente tenha só Conversas, e dentro dela só o que é dele
// (as suas, participações e menções). Fila por time, por canal, não atribuídas e pastas são
// gestão. O backoffice é o agente mais as pastas de pendência, que é o material de trabalho dele.
const { perfil, ehAdmin, papelNativo, preVisualizando, verComo } =
  useFandataPerfil();

// A saída de emergência do mapa de perfis depende do papel NATIVO: é o que garante que um
// administrador que GRAVOU para si o perfil Agente continue com Configurações → Agentes para
// desfazer a escolha.
//
// ⚠️ ELA NÃO VALE DURANTE A PRÉ-VISUALIZAÇÃO, e a correção é de 03/10. Na pré-visualização a
// saída já existe e é melhor: o botão "Voltar" na faixa do topo, que não depende de tela
// nenhuma do menu. Manter a exceção ali só sujava a conferência, e foi exatamente o que a
// Tamara relatou ao validar os perfis em 02/10: "quando alterei, vi que o agente tem acesso às
// configurações da plataforma (essa opção não pode estar disponível nesse perfil)".
//
// Ou seja: quem pré-visualiza vê a tela do outro SEM distorção, e quem se deu o perfil de
// verdade continua com como voltar.
const ehAdminNativo = computed(
  () => papelNativo.value === 'administrator' && !preVisualizando.value
);

const nomeDoPerfilVisto = computed(() =>
  preVisualizando.value
    ? t(`AGENT_MGMT.PERFIS.${preVisualizando.value.toUpperCase()}`)
    : ''
);

// T-224 e T-237 (pedidos de 24/09): por que a barra do AGENTE tem só Minhas Conversas e
// Menções. 'Labels' saiu porque "o Bira não etiqueta, ele tabula de acordo com a taxonomia" (a
// etiqueta continua sendo gravada por baixo, é dela que saem o Farol e a ordenação por
// criticidade, mas deixa de abrir a lista das 35 para quem atende); 'All' saiu porque a fila do
// agente é a dele, e ver tudo é leitura de supervisão. Quem faz valer isso agora é o mapa em
// `helper/fandataPerfis.js`, onde o perfil `agente` lista só esses dois filhos.

// Pedidos de 24/09. Estes somem para TODO MUNDO, inclusive administrador, porque o problema não
// é permissão, é a entrada existir:
//
// · 'Unattended' (T-274) é redundante com "não atribuído": duas portas para a mesma fila fazem
//   quem atende achar que são coisas diferentes.
// · 'Active' (T-276) é contato ativo x inativo, conceito de plano de ferramenta que o clube não
//   usa: "podemos remover", nas palavras da Tamara.
// · 'Tagged With' (T-276) abre a lista de etiquetas dentro de Contatos, e etiqueta saiu da
//   operação: quem classifica é a tabulação da taxonomia.
// · 'Labels' (03/10) abria a lista das 35 etiquetas dentro de Conversas. A etiqueta saiu da
//   operação: quem classifica é a tabulação da taxonomia, e deixar a porta aberta convida a
//   trabalhar pela dimensão errada. Pedido da Tamara em 02/10: "ainda existe o campo etiquetas,
//   precisa sair de todos os perfis". A etiqueta CONTINUA sendo gravada por baixo, porque é dela
//   que saem o Farol e a ordenação por criticidade: o que sai é a entrada no menu.
const FORA_PARA_TODOS = ['Unattended', 'Active', 'Tagged With', 'Labels'];

// T-263 (28/09): a decisão de quem vê o quê saiu daqui e foi para `helper/fandataPerfis.js`,
// como mapa explícito por perfil. Eram três perfis e passaram a ser seis, e regra espalhada em
// condicional ("se admin, senão se backoffice...") não sobrevive a seis: cada perfil novo
// obrigaria a reler todas as linhas para descobrir onde ele cai.
//
// O que continua aqui é só o que vale para TODO MUNDO, inclusive administrador, porque não é
// permissão e sim entrada que não deveria existir.
const podeVer = (item, secao) => {
  if (FORA_PARA_TODOS.includes(item.name)) return false;

  return perfilVeFilho(perfil.value, secao, item.name, ehAdminNativo.value);
};

const filtrarPorPerfil = itens =>
  itens
    .filter(item => perfilVeSecao(perfil.value, item.name, ehAdminNativo.value))
    .map(item =>
      item.children
        ? { ...item, children: item.children.filter(f => podeVer(f, item.name)) }
        : item
    );

const menuItems = computed(() => {
  return filtrarPorPerfil([
    {
      name: 'Inbox',
      label: t('SIDEBAR.INBOX'),
      icon: 'i-lucide-inbox',
      to: accountScopedRoute('inbox_view'),
      activeOn: ['inbox_view', 'inbox_view_conversation'],
      getterKeys: {
        count: 'notifications/getUnreadCount',
      },
    },
    {
      name: 'Conversation',
      label: t('SIDEBAR.CONVERSATIONS'),
      icon: 'i-lucide-message-circle',
      children: [
        {
          name: 'All',
          label: t('SIDEBAR.ALL_CONVERSATIONS'),
          icon: 'i-lucide-inbox',
          badgeCount: allUnreadCount.value,
          activeOn: ['inbox_conversation'],
          to: accountScopedRoute('home'),
        },
        {
          name: 'Mentions',
          label: t('SIDEBAR.MENTIONED_CONVERSATIONS'),
          icon: 'i-lucide-at-sign',
          activeOn: ['conversation_through_mentions'],
          to: accountScopedRoute('conversation_mentions'),
          badgeCount: mentionUnreadCount.value,
        },
        {
          name: 'Participating',
          // T-237: "Participantes" não diz nada ao agente. É a fila dele.
          label: t('SIDEBAR.MINHAS_CONVERSAS'),
          icon: 'i-lucide-user-round-check',
          activeOn: ['conversation_through_participating'],
          to: accountScopedRoute('conversation_participating'),
        },
        {
          // FanData (pedido do time, 07/10): o que EU já comecei a atender, separado do que
          // ainda nem olhei. Fica logo abaixo de Minhas Conversas porque é um recorte dela.
          name: 'EmAtendimento',
          label: t('SIDEBAR.CONVERSAS_EM_ATENDIMENTO'),
          icon: 'i-lucide-message-circle-more',
          activeOn: ['conversation_through_em_atendimento'],
          to: accountScopedRoute('conversation_em_atendimento'),
        },
        {
          name: 'Unattended',
          activeOn: ['conversation_through_unattended'],
          label: t('SIDEBAR.UNATTENDED_CONVERSATIONS'),
          icon: 'i-lucide-clock-alert',
          to: accountScopedRoute('conversation_unattended'),
        },
        {
          name: 'Folders',
          label: t('SIDEBAR.CUSTOM_VIEWS_FOLDER'),
          icon: 'i-lucide-folder',
          activeOn: ['conversations_through_folders'],
          ...buildSortConfig(SIDEBAR_SORT_SECTIONS.FOLDERS),
          collapsible: true,
          showTreeLine: true,
          children: sortedFolders.value.map(view => ({
            name: `${view.name}-${view.id}`,
            label: view.name,
            to: accountScopedRoute('folder_conversations', { id: view.id }),
          })),
        },
        {
          name: 'Teams',
          label: t('SIDEBAR.TEAMS'),
          icon: 'i-lucide-users',
          activeOn: ['conversations_through_team'],
          ...buildSortConfig(SIDEBAR_SORT_SECTIONS.TEAMS),
          collapsible: true,
          showTreeLine: true,
          children: sortedTeams.value.map(team => ({
            name: `${team.name}-${team.id}`,
            label: team.name,
            badgeCount: getTeamUnreadCount.value(team.id),
            to: accountScopedRoute('team_conversations', { teamId: team.id }),
          })),
        },
        {
          name: 'Channels',
          label: t('SIDEBAR.CHANNELS'),
          icon: 'i-lucide-mailbox',
          activeOn: ['conversation_through_inbox'],
          ...buildSortConfig(SIDEBAR_SORT_SECTIONS.CHANNELS),
          collapsible: true,
          showTreeLine: true,
          children: canaisPorUso.value,
        },
        {
          name: 'Labels',
          label: t('SIDEBAR.LABELS'),
          icon: 'i-lucide-tag',
          activeOn: ['conversations_through_label'],
          ...buildSortConfig(SIDEBAR_SORT_SECTIONS.LABELS),
          collapsible: true,
          showTreeLine: true,
          children: sortedLabels.value.map(label => ({
            name: `${label.title}-${label.id}`,
            label: label.title,
            badgeCount: getLabelUnreadCount.value(label.id),
            icon: h('span', {
              class: `size-[8px] rounded-sm`,
              style: { backgroundColor: label.color },
            }),
            to: accountScopedRoute('label_conversations', {
              label: label.title,
            }),
          })),
        },
      ],
    },
    {
      name: 'Contacts',
      label: t('SIDEBAR.CONTACTS'),
      icon: 'i-lucide-contact',
      children: [
        {
          name: 'All Contacts',
          label: t('SIDEBAR.ALL_CONTACTS'),
          to: accountScopedRoute(
            'contacts_dashboard_index',
            {},
            { page: 1, search: undefined }
          ),
          activeOn: ['contacts_dashboard_index', 'contacts_edit'],
        },
        {
          name: 'Active',
          label: t('SIDEBAR.ACTIVE'),
          to: accountScopedRoute('contacts_dashboard_active'),
          activeOn: ['contacts_dashboard_active'],
        },
        {
          name: 'Segments',
          icon: 'i-lucide-group',
          label: t('SIDEBAR.CUSTOM_VIEWS_SEGMENTS'),
          collapsible: true,
          showTreeLine: true,
          children: contactCustomViews.value.map(view => ({
            name: `${view.name}-${view.id}`,
            label: view.name,
            to: accountScopedRoute(
              'contacts_dashboard_segments_index',
              { segmentId: view.id },
              { page: 1 }
            ),
            activeOn: [
              'contacts_dashboard_segments_index',
              'contacts_edit_segment',
            ],
          })),
        },
        {
          name: 'Tagged With',
          icon: 'i-lucide-tag',
          label: t('SIDEBAR.TAGGED_WITH'),
          collapsible: true,
          showTreeLine: true,
          children: labels.value.map(label => ({
            name: `${label.title}-${label.id}`,
            label: label.title,
            icon: h('span', {
              class: `size-[8px] rounded-sm`,
              style: { backgroundColor: label.color },
            }),
            to: accountScopedRoute(
              'contacts_dashboard_labels_index',
              { label: label.title },
              { page: 1, search: undefined }
            ),
            activeOn: [
              'contacts_dashboard_labels_index',
              'contacts_edit_label',
            ],
          })),
        },
      ],
    },
    {
      // T-151: a árvore de relatórios encolheu. Criticidade, CSAT e o relatório nativo do Bot
      // saíram daqui e viraram ABA dentro de Farol, Satisfação e Atendimento IA. Nada de
      // informação se perdeu; o que sumiu foi a repetição, que era a reclamação dela.
      name: 'Reports',
      label: t('SIDEBAR.REPORTS'),
      icon: 'i-lucide-chart-spline',
      children: [
        {
          name: 'Report Farol',
          label: t('SIDEBAR.REPORTS_FAROL'),
          to: accountScopedRoute('farol_reports'),
          badge: fandataGated ? 'EM BREVE' : undefined,
        },
        {
          name: 'Report IA Impact',
          label: t('SIDEBAR.REPORTS_IA_IMPACT'),
          to: accountScopedRoute('bot_impact_reports'),
          badge: fandataGated ? 'EM BREVE' : undefined,
        },
        {
          name: 'Report Jornada',
          label: t('SIDEBAR.REPORTS_JORNADA'),
          to: accountScopedRoute('journey_reports'),
          badge: fandataGated ? 'EM BREVE' : undefined,
        },
        {
          name: 'Report Satisfacao',
          label: t('SIDEBAR.REPORTS_SATISFACAO'),
          to: accountScopedRoute('satisfacao_reports'),
          badge: fandataGated ? 'EM BREVE' : undefined,
        },
        {
          name: 'Report Pendencia',
          label: t('SIDEBAR.REPORTS_PENDENCIA'),
          to: accountScopedRoute('pendencia_reports'),
          badge: fandataGated ? 'EM BREVE' : undefined,
        },
        {
          name: 'Report Presenca',
          label: t('SIDEBAR.REPORTS_PRESENCA'),
          to: accountScopedRoute('presenca_reports'),
          badge: fandataGated ? 'EM BREVE' : undefined,
        },
        {
          name: 'Report Overview',
          label: t('SIDEBAR.REPORTS_OVERVIEW'),
          to: accountScopedRoute('account_overview_reports'),
        },
        {
          name: 'Report Conversation',
          label: t('SIDEBAR.REPORTS_CONVERSATION'),
          to: accountScopedRoute('conversation_reports'),
        },
        ...reportRoutes.value,
        // T-255: o SLA saiu do menu e virou COLUNA na tela de Quebras, onde já se lê métrica
        // por agente, time, caixa e etiqueta. Uma página só para ele obrigava a abrir outra
        // tela e cruzar de cabeça com a performance de quem atendeu. A rota continua de pé
        // para link antigo não morrer.
      ],
    },
    {
      name: 'Settings',
      label: t('SIDEBAR.SETTINGS'),
      icon: 'i-lucide-bolt',
      // Em quatro conjuntos, e dentro de cada um em ordem alfabética pelo rótulo em português.
      // A lista plana crescia por ordem de chegada, e achar "Etiquetas" virava varredura visual.
      children: [
        // o Bira e o que ele diz
        {
          name: 'Settings Bira Auditoria',
          label: t('SIDEBAR.FANDATA_AUDITORIA'),
          icon: 'i-lucide-scroll-text',
          to: accountScopedRoute('fandata_auditoria'),
          badge: fandataGated ? 'EM BREVE' : undefined,
        },
        {
          name: 'Settings Bira Base',
          label: t('SIDEBAR.CAPITAO_CONTEUDO_V2'),
          icon: 'i-lucide-library',
          to: accountScopedRoute('capitao_base'),
          badge: fandataGated ? 'EM BREVE' : undefined,
        },
        {
          name: 'Settings Capitao',
          label: t('SIDEBAR.CAPITAO'),
          icon: 'i-lucide-sparkles',
          to: accountScopedRoute('capitao_calibracao'),
          badge: fandataGated ? 'EM BREVE' : undefined,
        },
        // o atendimento
        {
          name: 'Settings Agents',
          label: t('SIDEBAR.AGENTS'),
          icon: 'i-lucide-square-user',
          to: accountScopedRoute('agent_list'),
        },
        {
          name: 'Settings Automation',
          label: t('SIDEBAR.AUTOMATION'),
          icon: 'i-lucide-repeat',
          to: accountScopedRoute('automation_list'),
        },
        {
          name: 'Settings Inboxes',
          label: t('SIDEBAR.INBOXES'),
          icon: 'i-lucide-inbox',
          activeOn: [
            'settings_inbox_list',
            'settings_inbox_show',
            'settings_inbox_new',
            'settings_inbox_finish',
            'settings_inboxes_page_channel',
            'settings_inboxes_add_agents',
          ],
          to: accountScopedRoute('settings_inbox_list'),
        },
        {
          name: 'Conversation Workflow',
          label: t('SIDEBAR.CONVERSATION_WORKFLOW'),
          icon: 'i-lucide-workflow',
          to: accountScopedRoute('conversation_workflow_index'),
        },
        {
          name: 'Settings Macros',
          label: t('SIDEBAR.MACROS'),
          icon: 'i-lucide-toy-brick',
          to: accountScopedRoute('macros_wrapper'),
        },
        {
          name: 'Settings Canned Responses',
          label: t('SIDEBAR.CANNED_RESPONSES'),
          icon: 'i-lucide-message-square-quote',
          to: accountScopedRoute('canned_list'),
        },
        {
          name: 'Settings Agent Bots',
          label: t('SIDEBAR.AGENT_BOTS'),
          icon: 'i-lucide-bot',
          to: accountScopedRoute('agent_bots'),
        },
        {
          name: 'Settings Sla',
          label: t('SIDEBAR.SLA'),
          icon: 'i-lucide-clock-alert',
          to: accountScopedRoute('sla_list'),
        },
        {
          name: 'Settings Teams',
          label: t('SIDEBAR.TEAMS'),
          icon: 'i-lucide-users',
          activeOn: [
            'settings_teams_list',
            'settings_teams_new',
            'settings_teams_finish',
            'settings_teams_add_agents',
            'settings_teams_show',
            'settings_teams_edit',
            'settings_teams_edit_members',
            'settings_teams_edit_finish',
          ],
          to: accountScopedRoute('settings_teams_list'),
        },
        {
          name: 'Settings Agent Assignment',
          label: t('SIDEBAR.AGENT_ASSIGNMENT'),
          icon: 'i-lucide-user-cog',
          activeOn: [
            'assignment_policy_index',
            'agent_assignment_policy_index',
            'agent_assignment_policy_create',
            'agent_assignment_policy_edit',
            'agent_capacity_policy_index',
            'agent_capacity_policy_create',
            'agent_capacity_policy_edit',
          ],
          to: accountScopedRoute('assignment_policy_index'),
        },
        // os dados do clube
        {
          name: 'Settings Custom Attributes',
          label: t('SIDEBAR.CUSTOM_ATTRIBUTES'),
          icon: 'i-lucide-code',
          to: accountScopedRoute('attributes_list'),
        },
        {
          name: 'Settings Duplicados',
          label: t('SIDEBAR.DUPLICADOS'),
          icon: 'i-lucide-merge',
          to: accountScopedRoute('fandata_duplicados'),
        },
        {
          name: 'Settings Labels',
          label: t('SIDEBAR.LABELS'),
          icon: 'i-lucide-tags',
          to: accountScopedRoute('labels_list'),
        },
        {
          name: 'Settings Modelos de e-mail',
          label: t('SIDEBAR.MODELOS_EMAIL'),
          icon: 'i-lucide-mail',
          to: accountScopedRoute('fandata_email_templates'),
        },
        {
          name: 'Settings Parceiros',
          label: t('SIDEBAR.PARCEIROS'),
          icon: 'i-lucide-handshake',
          to: accountScopedRoute('fandata_parceiros'),
        },
        // a conta
        {
          name: 'Settings Account Settings',
          label: t('SIDEBAR.ACCOUNT_SETTINGS'),
          icon: 'i-lucide-briefcase',
          to: accountScopedRoute('general_settings_index'),
        },
        {
          name: 'Settings Integrations',
          label: t('SIDEBAR.INTEGRATIONS'),
          icon: 'i-lucide-blocks',
          to: accountScopedRoute('settings_applications'),
        },
        {
          name: 'Settings Security',
          label: t('SIDEBAR.SECURITY'),
          icon: 'i-lucide-shield',
          to: accountScopedRoute('security_settings_index'),
        },
      ],
    },
  ]);
});
</script>

<template>
  <aside
    v-on-click-outside="[
      closeMobileSidebar,
      {
        ignore: [
          '#mobile-sidebar-launcher',
          '[data-popover-content]',
          '[data-popover-backdrop]',
        ],
      },
    ]"
    class="bg-n-background flex flex-col text-sm pb-px fixed top-0 ltr:left-0 rtl:right-0 h-full z-40 w-[200px] md:w-auto md:relative md:flex-shrink-0 md:ltr:translate-x-0 md:rtl:translate-x-0 ltr:border-r rtl:border-l border-n-weak"
    :class="[
      {
        'shadow-lg md:shadow-none': isMobileSidebarOpen,
        'ltr:-translate-x-full rtl:translate-x-full': !isMobileSidebarOpen,
        'transition-transform duration-200 ease-out md:transition-[width]':
          !isResizing,
      },
    ]"
    :style="isMobile ? undefined : { width: `${sidebarWidth}px` }"
  >
    <section
      class="grid"
      :class="isEffectivelyCollapsed ? 'mt-3 mb-6 gap-4' : 'mt-1 mb-4 gap-2'"
    >
      <div
        class="flex gap-2 items-center min-w-0"
        :class="{
          'justify-center px-1': isEffectivelyCollapsed,
          'px-2': !isEffectivelyCollapsed,
        }"
      >
        <template v-if="isEffectivelyCollapsed">
          <SidebarAccountSwitcher
            is-collapsed
            @show-create-account-modal="emit('showCreateAccountModal')"
          />
        </template>
        <template v-else>
          <div class="grid flex-shrink-0 place-content-center size-6">
            <Logo class="size-4" />
          </div>
          <div class="flex-shrink-0 w-px h-3 bg-n-strong" />
          <SidebarAccountSwitcher
            class="flex-grow -mx-1 min-w-0"
            @show-create-account-modal="emit('showCreateAccountModal')"
          />
        </template>
      </div>
      <div
        class="flex gap-2"
        :class="isEffectivelyCollapsed ? 'flex-col items-center' : 'px-2'"
      >
        <RouterLink
          v-if="!isEffectivelyCollapsed"
          :to="{ name: 'search' }"
          class="flex gap-2 items-center px-2 py-1 w-full h-7 rounded-lg outline outline-1 outline-n-weak bg-n-button-color transition-all duration-100 ease-out"
        >
          <span class="flex-shrink-0 i-lucide-search size-4 text-n-slate-10" />
          <span class="flex-grow text-start text-n-slate-10">
            {{ t('COMBOBOX.SEARCH_PLACEHOLDER') }}
          </span>
          <span
            class="hidden tracking-wide pointer-events-none select-none text-n-slate-10"
          >
            {{ searchShortcut }}
          </span>
        </RouterLink>
        <RouterLink
          v-else
          :to="{ name: 'search' }"
          class="flex items-center justify-center size-8 rounded-lg outline outline-1 outline-n-weak bg-n-button-color transition-all duration-100 ease-out hover:bg-n-alpha-2 dark:hover:bg-n-slate-9/30"
          :title="t('COMBOBOX.SEARCH_PLACEHOLDER')"
        >
          <span class="i-lucide-search size-4 text-n-slate-11" />
        </RouterLink>
        <!-- T-237: iniciar conversa é ação de gestão, não de quem está na fila. -->
        <ComposeConversation v-if="ehAdmin" align="start">
          <template #trigger="{ isOpen }">
            <Button
              icon="i-lucide-pen-line"
              color="slate"
              size="sm"
              class="dark:hover:!bg-n-slate-9/30"
              :class="[
                isEffectivelyCollapsed
                  ? '!size-8 !outline-n-weak !text-n-slate-11'
                  : '!h-7 !outline-n-weak !text-n-slate-11',
                { '!bg-n-alpha-2 dark:!bg-n-slate-9/30': isOpen },
              ]"
            />
          </template>
        </ComposeConversation>
      </div>
      <!-- Faixa da pré-visualização de perfil. Fica no topo, e não no menu que a liga, porque
           é o aviso de que o menu não é o de verdade: sem ela dá para esquecer que está
           pré-visualizando e achar que a barra lateral quebrou. É também o botão de voltar. -->
      <div
        v-if="preVisualizando"
        class="mx-2 rounded-lg bg-n-amber-3 text-n-amber-12 border border-n-amber-5"
        :class="isEffectivelyCollapsed ? 'p-1' : 'px-2 py-1.5'"
      >
        <button
          v-if="isEffectivelyCollapsed"
          class="grid place-content-center w-full rounded-md size-6"
          :title="
            t('SIDEBAR_ITEMS.VER_COMO.AVISO', { perfil: nomeDoPerfilVisto })
          "
          @click="verComo(null)"
        >
          <span class="i-lucide-eye size-4" />
        </button>
        <template v-else>
          <div class="flex gap-1.5 items-center min-w-0">
            <span class="flex-shrink-0 i-lucide-eye size-3.5" />
            <span class="flex-grow text-xs font-medium truncate">
              {{
                t('SIDEBAR_ITEMS.VER_COMO.AVISO', {
                  perfil: nomeDoPerfilVisto,
                })
              }}
            </span>
            <button
              class="flex-shrink-0 text-xs underline underline-offset-2"
              @click="verComo(null)"
            >
              {{ t('SIDEBAR_ITEMS.VER_COMO.SAIR') }}
            </button>
          </div>
          <p class="m-0 mt-0.5 text-xs opacity-80">
            {{ t('SIDEBAR_ITEMS.VER_COMO.RESSALVA') }}
          </p>
        </template>
      </div>
    </section>
    <nav
      class="grid overflow-y-scroll flex-grow gap-2 pb-5 no-scrollbar min-w-0"
      :class="isEffectivelyCollapsed ? 'px-1' : 'px-2'"
    >
      <ul
        class="flex flex-col gap-1 m-0 list-none min-w-0"
        :class="{ 'items-center': isEffectivelyCollapsed }"
      >
        <SidebarGroup
          v-for="item in menuItems"
          :key="item.name"
          v-bind="item"
        />
      </ul>
    </nav>
    <section
      class="flex relative flex-col flex-shrink-0 gap-1 justify-between items-center"
    >
      <div
        class="pointer-events-none absolute inset-x-0 -top-[1.938rem] h-8 bg-gradient-to-t from-n-background to-transparent"
      />
      <SidebarChangelogCard
        v-if="
          isOnChatwootCloud &&
          !isACustomBrandedInstance &&
          !isEffectivelyCollapsed
        "
      />
      <SidebarChangelogButton
        v-if="
          isOnChatwootCloud &&
          !isACustomBrandedInstance &&
          isEffectivelyCollapsed
        "
      />
      <div
        class="px-1 py-1.5 flex-shrink-0 flex w-full z-50 gap-2 items-center border-t border-n-weak shadow-[0px_-2px_4px_0px_rgba(27,28,29,0.02)]"
        :class="isEffectivelyCollapsed ? 'justify-center' : 'justify-between'"
      >
        <SidebarProfileMenu
          :is-collapsed="isEffectivelyCollapsed"
          @open-key-shortcut-modal="emit('openKeyShortcutModal')"
        />
      </div>
    </section>
    <!-- Resize Handle (desktop only) -->
    <div
      class="hidden md:block absolute top-0 h-full w-1 cursor-col-resize z-40 ltr:right-0 rtl:left-0 group"
      @mousedown="onResizeStart"
      @touchstart="onResizeStart"
      @dblclick="onResizeHandleDoubleClick"
    >
      <div
        class="absolute top-0 h-full w-px ltr:right-0 rtl:left-0 bg-transparent group-hover:bg-n-brand transition-colors"
        :class="{ 'bg-n-brand': isResizing }"
      />
    </div>
  </aside>
</template>
